import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';

interface LoginForm {
  username: string;
  password: string;
}

const Login: React.FC = () => {
  const [form, setForm] = useState<LoginForm>({
    username: '',
    password: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  const navigate = useNavigate();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Xóa lỗi field khi user bắt đầu nhập
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Xóa error message chung
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};
    
    if (!form.username.trim()) {
      errors.username = 'Username is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.username)) {
      errors.username = 'Please enter a valid email address';
    }
    
    if (!form.password.trim()) {
      errors.password = 'Password is required';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setErrorMessage('');
    
    try {
      const user = await authService.login({
        username: form.username,
        password: form.password
      });
      
      // Login thành công
      console.log('Login successful:', user);
      
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (error: any) {
      setErrorMessage(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#f9fafb', 
      display: 'flex', 
      flexDirection: 'column', 
      justifyContent: 'center', 
      padding: '3rem 1.5rem' 
    }}>
      <div style={{ margin: '0 auto', width: '100%', maxWidth: '28rem' }}>
        {/* Logo */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{
            width: '5rem',
            height: '5rem',
            background: 'linear-gradient(to right, #9333ea, #2563eb)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <span style={{ color: 'white', fontSize: '1.5rem', fontWeight: 'bold' }}>HL</span>
          </div>
        </div>
        
        {/* Title */}
        <h2 style={{
          marginTop: '1.5rem',
          textAlign: 'center',
          fontSize: '1.875rem',
          fontWeight: '800',
          color: '#111827'
        }}>
          Heartline Admin - Welcome Back
        </h2>
        <p style={{
          marginTop: '0.5rem',
          textAlign: 'center',
          fontSize: '0.875rem',
          color: '#6b7280'
        }}>
          Sign in to your admin account
        </p>
      </div>

      <div style={{ marginTop: '2rem', margin: '2rem auto 0', width: '100%', maxWidth: '28rem' }}>
        <div style={{
          backgroundColor: 'white',
          padding: '2rem 1rem',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          borderRadius: '0.5rem'
        }}>
          <form onSubmit={handleSubmit}>
            {/* Username Input */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="username" style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#374151'
              }}>
                Email Address
              </label>
              <div style={{ marginTop: '0.25rem' }}>
                <input
                  id="username"
                  name="username"
                  type="email"
                  autoComplete="username"
                  required
                  value={form.username}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                  style={{
                    appearance: 'none',
                    display: 'block',
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    border: `1px solid ${fieldErrors.username ? '#fca5a5' : '#d1d5db'}`,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    lineHeight: '1.25rem',
                    boxSizing: 'border-box'
                  }}
                />
                {fieldErrors.username && (
                  <p style={{
                    marginTop: '0.25rem',
                    fontSize: '0.875rem',
                    color: '#dc2626'
                  }}>{fieldErrors.username}</p>
                )}
              </div>
            </div>

            {/* Password Input */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="password" style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#374151'
              }}>
                Password
              </label>
              <div style={{ marginTop: '0.25rem', position: 'relative' }}>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={form.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  style={{
                    appearance: 'none',
                    display: 'block',
                    width: '100%',
                    padding: '0.5rem 2.5rem 0.5rem 0.75rem',
                    border: `1px solid ${fieldErrors.password ? '#fca5a5' : '#d1d5db'}`,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    lineHeight: '1.25rem',
                    boxSizing: 'border-box'
                  }}
                />
                
                {/* Show/Hide Password Button */}
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {showPassword ? (
                    <svg style={{ height: '1.25rem', width: '1.25rem', color: '#9ca3af' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L12 12m-2.122-2.122L7.758 7.758M12 12l2.122-2.122L16.242 7.758M12 12l-2.122 2.122M7.758 7.758l2.122 2.122m0 0L12 12m-2.122-2.122l-4.242-4.242" />
                    </svg>
                  ) : (
                    <svg style={{ height: '1.25rem', width: '1.25rem', color: '#9ca3af' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
                
                {fieldErrors.password && (
                  <p style={{
                    marginTop: '0.25rem',
                    fontSize: '0.875rem',
                    color: '#dc2626'
                  }}>{fieldErrors.password}</p>
                )}
              </div>
            </div>

            {/* Error Message Area */}
            {errorMessage && (
              <div style={{
                borderRadius: '0.375rem',
                backgroundColor: '#fef2f2',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flexShrink: 0 }}>
                    <svg style={{ height: '1.25rem', width: '1.25rem', color: '#f87171' }} viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div style={{ marginLeft: '0.75rem' }}>
                    <h3 style={{
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: '#991b1b',
                      margin: 0
                    }}>
                      {errorMessage}
                    </h3>
                  </div>
                </div>
              </div>
            )}

            {/* Login Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '0.5rem 1rem',
                  border: 'none',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  borderRadius: '0.375rem',
                  color: 'white',
                  backgroundColor: isLoading ? '#9ca3af' : '#4f46e5',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={!isLoading ? (e) => (e.target as HTMLButtonElement).style.backgroundColor = '#4338ca' : undefined}
                onMouseLeave={!isLoading ? (e) => (e.target as HTMLButtonElement).style.backgroundColor = '#4f46e5' : undefined}
              >
                {isLoading ? (
                  <>
                    <svg style={{
                      animation: 'spin 1s linear infinite',
                      marginLeft: '-0.25rem',
                      marginRight: '0.75rem',
                      height: '1.25rem',
                      width: '1.25rem',
                      color: 'white'
                    }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </>
                ) : (
                  'LOGIN'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default Login; 